import React, { createContext, useContext, ReactNode } from 'react';
import { EnhancedTableColumn } from '../types';
import type { FilterUIConfig } from '../types';

/**
 * Table context interface containing all shared table state and handlers
 */
export interface TableContextValue<T extends Record<string, unknown>> {
  // Data and rows
  rows: T[];
  actualRows: T[];
  paginatedRows: T[];
  getRowId: (row: T, index?: number) => string | number;

  // Columns
  columns: EnhancedTableColumn<T>[];
  visibleColumns: EnhancedTableColumn<T>[];
  orderedColumns: EnhancedTableColumn<T>[];

  // Selection
  selection: {
    selected: (string | number)[];
    handleSelect: (id: string | number) => void;
    handleSelectAll: (rows: T[], getRowId: (row: T) => string | number) => void;
  };

  // Expansion
  expansion: {
    expanded: Record<string | number, boolean>;
    onRowExpand: (rowId: string | number) => void;
  };

  // Column resizing
  columnResize: {
    columnWidths: Record<string, number>;
    handleColumnResize: (columnId: string, width: number) => void;
    getColumnWidth: (columnId: string) => number | undefined;
  };

  // Column reordering
  columnReorder: {
    columnOrder: string[];
    handleColumnReorder: (fromIndex: number, toIndex: number) => void;
    getOrderedColumns: <T>(
      columns: EnhancedTableColumn<T>[],
    ) => EnhancedTableColumn<T>[];
  };

  // Column visibility
  columnVisibility: {
    visibility: Record<string, boolean>;
    onColumnVisibilityChange: (columnId: string, visible: boolean) => void;
  };

  // Keyboard navigation
  keyboardNavigation?: {
    focusedCell: { rowIndex: number; columnIndex: number } | null;
    handleKeyDown: (event: React.KeyboardEvent) => void;
    setFocusedCell: (rowIndex: number, columnIndex: number) => void;
  };

  // Inline editing
  inlineEdit?: {
    editingCell: { rowId: string | number; field: keyof T } | null;
    editingValue: unknown;
    isEditing: (rowId: string | number, field: keyof T) => boolean;
    startEdit: (rowId: string | number, field: keyof T, value: unknown) => void;
    commitEdit: (value: unknown) => void;
    cancelEdit: () => void;
    updateEditingValue: (value: unknown) => void;
  };

  // Filtering
  filtering: {
    filters: Array<{ field: string; operator: string; value: unknown }>;
    addFilter: (filter: {
      field: string;
      operator: string;
      value: unknown;
    }) => void;
    removeFilter: (index: number) => void;
    clearFilters: () => void;
    hasActiveFilters: boolean;
  };

  // Server-side data
  serverData?: {
    data: T[];
    total: number;
    isLoading: boolean;
    error?: Error;
    queryParams: {
      search: string;
      filters: Array<{ field: string; operator: string; value: unknown }>;
      sorting?: { field: string; direction: 'asc' | 'desc' };
      pagination?: { page: number; pageSize: number };
    };
    setSearch: (value: string) => void;
    setFilters: (
      filters: Array<{ field: string; operator: string; value: unknown }>,
    ) => void;
    setSorting: (sorting: { field: string; direction: 'asc' | 'desc' }) => void;
    setPagination: (pagination: { page: number; pageSize: number }) => void;
  };

  // Search
  searchValue: string;
  setSearchValue: (value: string) => void;

  // States
  isLoading: boolean;
  error?: Error | string | React.ReactNode;
  isServerSide: boolean;

  // Configuration flags
  showHeader: boolean;
  showFilters: boolean;
  showBulkActions: boolean;
  dense: boolean;
  stickyHeader: boolean;
  // Filter UI configuration for building filter controls
  filterConfigs?: FilterUIConfig[];

  // Event handlers
  onSearch?: (value: string) => void;
  onSort: (property: keyof T) => void;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void;
}

/**
 * Table context for sharing state between table components
 */
export const TableContext = createContext<TableContextValue<
  Record<string, unknown>
> | null>(null);

/**
 * Hook to access table context
 */
export function useTableContext<
  T extends Record<string, unknown>,
>(): TableContextValue<T> {
  const context = useContext(TableContext);
  if (!context) {
    throw new Error('useTableContext must be used within a TableProvider');
  }
  return context as TableContextValue<T>;
}

/**
 * Table provider props
 */
export interface TableProviderProps<T extends Record<string, unknown>> {
  children: ReactNode;
  value: TableContextValue<T>;
}

/**
 * Table provider component that shares state across all table components
 */
export function TableProvider<T extends Record<string, unknown>>({
  children,
  value,
}: TableProviderProps<T>) {
  return (
    <TableContext.Provider
      value={value as TableContextValue<Record<string, unknown>>}
    >
      {children}
    </TableContext.Provider>
  );
}
